"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
  type FC,
} from "react";
import { useAuth, type AuthUser } from "./AuthProvider";
import { apiFetch } from "../lib/api";

// ─── Pluggable onboarding step interface ─────────────────────────────────────

export interface OnboardingStep {
  /** Unique identifier for this step */
  id: string;
  /** Returns true if this step needs to be completed */
  check: (user: AuthUser) => boolean;
  /** The component to render for this step. Receives `onComplete` callback. */
  component: FC<{ onComplete: () => void }>;
}

interface OnboardingContextValue {
  /** Whether onboarding is currently in progress (blocking the UI) */
  onboarding: boolean;
}

const OnboardingContext = createContext<OnboardingContextValue>({
  onboarding: false,
});

export function useOnboarding() {
  return useContext(OnboardingContext);
}

// ─── Default steps ───────────────────────────────────────────────────────────

import { DisplayNameStep } from "../components/DisplayNameStep";

const DEFAULT_STEPS: OnboardingStep[] = [
  {
    id: "display-name",
    check: (user) => !user.displayName,
    component: DisplayNameStep,
  },
];

// ─── Provider ────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode;
  steps?: OnboardingStep[];
}

export function OnboardingProvider({ children, steps = DEFAULT_STEPS }: Props) {
  const { user, token, isNewLogin, clearNewLogin, refreshUser } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState<number | null>(null);
  const [checked, setChecked] = useState(false);
  const claimedRef = useRef(false);

  // Claim pending invites and then clear new login flag
  const finishOnboarding = useCallback(async () => {
    if (claimedRef.current) return;
    claimedRef.current = true;

    // Claim any pending invites for this phone number
    if (token && isNewLogin) {
      try {
        await apiFetch("/invites/claim", token, { method: "POST" });
        // Refresh user to update hasAccess after claiming invites
        await refreshUser();
      } catch {
        // Ignore — user may have no pending invites
      }
    }

    clearNewLogin();
  }, [token, isNewLogin, clearNewLogin, refreshUser]);

  // Run onboarding checks when user is available
  useEffect(() => {
    if (!user || checked) return;

    const pendingIndex = steps.findIndex((step) => step.check(user));
    if (pendingIndex >= 0) {
      setCurrentStepIndex(pendingIndex);
    } else {
      // No steps needed — claim invites and clear
      if (isNewLogin) finishOnboarding();
    }
    setChecked(true);
  }, [user, steps, checked, isNewLogin, finishOnboarding]);

  const handleStepComplete = useCallback(() => {
    setCurrentStepIndex((prev) => {
      if (prev === null) return null;
      // Find next pending step after current
      const nextIndex = steps.findIndex(
        (step, i) => i > prev && user && step.check(user)
      );
      if (nextIndex >= 0) return nextIndex;
      // All steps done — claim invites and clear
      if (isNewLogin) finishOnboarding();
      return null;
    });
  }, [steps, user, isNewLogin, finishOnboarding]);

  const onboarding = currentStepIndex !== null;
  const StepComponent = onboarding ? steps[currentStepIndex].component : null;

  // On sign-in (isNewLogin): block the UI entirely with the onboarding step
  // On page refresh (!isNewLogin): show app behind with modal overlay
  return (
    <OnboardingContext.Provider value={{ onboarding }}>
      {isNewLogin && onboarding ? (
        // Synchronous: block the UI until onboarding is complete
        <div style={fullScreenStyle}>
          {StepComponent && <StepComponent onComplete={handleStepComplete} />}
        </div>
      ) : (
        <>
          {children}
          {/* Async: overlay modal on page refresh if onboarding is needed */}
          {!isNewLogin && onboarding && StepComponent && (
            <div style={overlayStyle}>
              <div style={modalStyle}>
                <StepComponent onComplete={handleStepComplete} />
              </div>
            </div>
          )}
        </>
      )}
    </OnboardingContext.Provider>
  );
}

const fullScreenStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
  background: "#0a0a0a",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.75)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: "#111",
  border: "1px solid #333",
  borderRadius: 8,
  padding: "2rem",
  maxWidth: 400,
  width: "90%",
};
